import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Upload } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";
import { Modal, useToast } from "./overlay";
import { ErrorNote } from "./ui";
import { t, tn } from "../i18n";

interface Result {
  created: string[];
  existing: string[];
  invalid: { input: string; error: string }[];
  jobs: number[];
  batch_id?: number;
}

/** One place to add domains: a single name or a whole list, optionally recovered automatically. */
export function AddDomainsModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const toast = useToast();
  const [text, setText] = useState("");
  const [auto, setAuto] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const lines = text.split(/[\n,;\t]+/).map((s) => s.trim()).filter(Boolean);

  const submit = useMutation({
    mutationFn: () =>
      auto
        ? api.post<Result>("/api/batches", { name: `Batch ${new Date().toLocaleString()}`, text, auto_recover: true, mode: "best" })
        : api.post<Result>("/api/domains/import", { text, analyze: true }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["domains"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      const added = r.created.length;
      if (!r.invalid.length && lines.length === 1 && added + r.existing.length === 1) {
        toast(added ? t("{name} added — analysis started", { name: lines[0] }) : t("{name} is already in your list", { name: lines[0] }));
        close();
        return;
      }
      toast(tn(added, "{n} domain added", "{n} domains added"));
      setResult(r);
    },
  });

  const upload = async (f: File) => setText((await f.text()).trim());

  function close() {
    setText("");
    setResult(null);
    setAuto(false);
    submit.reset();
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title={t("Add domains")}
      description={t("Enter one domain or paste a list. Each domain is analyzed automatically.")}
      footer={
        result ? (
          <>
            {result.batch_id && <button className="btn-secondary" onClick={() => { close(); nav(`/batches/${result.batch_id}`); }}>{t("View batch progress")}</button>}
            <button className="btn-primary" onClick={close}>{t("Done")}</button>
          </>
        ) : (
          <>
            <button className="btn-secondary" onClick={close}>{t("Cancel")}</button>
            <button className="btn-primary" disabled={!lines.length || submit.isPending} onClick={() => submit.mutate()}>
              {submit.isPending ? t("Adding…") : lines.length > 1 ? t("Add {n} domains", { n: lines.length }) : t("Add domain")}
            </button>
          </>
        )
      }
    >
      {result ? (
        <div className="space-y-3 text-sm">
          <p>
            <b>{result.created.length}</b> {t("added")} · <b>{result.existing.length}</b> {t("already in your list")} · <b>{result.jobs.length}</b> {t("analyses started")}
            {auto && " — " + t("recovery starts automatically after each analysis")}.
          </p>
          {result.invalid.length > 0 && (
            <div>
              <div className="label">{t("Skipped")}</div>
              <ul className="space-y-1 text-xs text-red-600">{result.invalid.map((i) => <li key={i.input}><span className="mono">{i.input}</span>: {i.error}</li>)}</ul>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="label" htmlFor="add-domains">{t("Domains")}</label>
            <textarea id="add-domains" autoFocus className="input h-32 font-mono" placeholder={"beuniquebridal.com\nexample.org"} value={text} onChange={(e) => setText(e.target.value)} />
            <div className="mt-1.5 flex items-center justify-between text-xs text-slate-500">
              <span>{lines.length ? tn(lines.length, "{n} domain", "{n} domains") : t("One per line — URLs are fine too")}</span>
              <label className="inline-flex cursor-pointer items-center gap-1 text-brand-600 hover:underline">
                <Upload className="h-3.5 w-3.5" /> {t("Import CSV/TXT")}
                <input type="file" accept=".csv,.txt,text/plain,text/csv" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
              </label>
            </div>
          </div>
          <label className="flex items-start gap-3 rounded-xl border border-slate-200 p-3 text-sm dark:border-slate-700">
            <input type="checkbox" className="mt-0.5" checked={auto} onChange={(e) => setAuto(e.target.checked)} />
            <span>
              <span className="font-medium">{t("Recover automatically")}</span>
              <span className="block text-xs text-slate-500">{t("After analysis, restore each domain from its recommended period. You can always recover manually instead.")}</span>
            </span>
          </label>
          {submit.error && <ErrorNote error={submit.error} />}
        </div>
      )}
    </Modal>
  );
}
